import type { AccountSession } from './api';

const accountSessionStorageKey = 'amoye-account-session-v1';

export interface StoredAccountSession {
  accessToken: string;
  expiresAt: number;
  product: AccountSession['product'];
}

/** Keep only the short-lived bearer token and its expiry in tab-scoped
 * storage. Account and taxpayer data are re-read from the server on refresh. */
export function storeAccountSession(session: AccountSession, now = Date.now()): void {
  const value: StoredAccountSession = { accessToken: session.access_token,
    expiresAt: now + session.expires_in * 1000, product: session.product };
  try { window.sessionStorage.setItem(accountSessionStorageKey, JSON.stringify(value)); }
  catch { /* A privacy-restricted browser can still use the in-memory session. */ }
}

export function readAccountSession(now = Date.now()): StoredAccountSession | undefined {
  try {
    const raw = window.sessionStorage.getItem(accountSessionStorageKey);
    if (!raw) return undefined;
    const value = JSON.parse(raw) as Partial<StoredAccountSession>;
    const validProduct = value.product === 'practitioner' || value.product === 'business' || value.product === 'individual';
    if (typeof value.accessToken !== 'string' || value.accessToken.length === 0
      || typeof value.expiresAt !== 'number' || !Number.isFinite(value.expiresAt)
      || value.expiresAt <= now || !validProduct) {
      clearAccountSession(); return undefined;
    }
    return value as StoredAccountSession;
  } catch {
    clearAccountSession(); return undefined;
  }
}

export function clearAccountSession(): void {
  try { window.sessionStorage.removeItem(accountSessionStorageKey); }
  catch { /* The in-memory sign-out still completes. */ }
}

